 import { useState, useEffect } from "react";
 import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, InputGroup, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import FormContainer from "../Components/FormContainer";
import Loader from '../Components/Loader';
import { useRegisterMutation } from '../slices/usersApiSlice';
import { useValidatePartnerCodeMutation } from '../slices/partnerCodesApiSlice';
import { setCredentials } from '../slices/authSlice';
import { toast } from 'react-toastify';
 
 
 const RegisterScreen = () => {
   const [name, setName] = useState('');
   const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [partnerCode, setPartnerCode] = useState('');
  const [partnerStatus, setPartnerStatus] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
     
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [register, { isLoading }] = useRegisterMutation();
  const [validatePartnerCode] = useValidatePartnerCodeMutation();
 
   const { userInfo } = useSelector((state) => state.auth);
 
   const { search } = useLocation();
   const sp = new URLSearchParams(search);
   const redirect = sp.get('redirect') || '/';
 
   useEffect(() => {
     if (userInfo) {
       navigate(redirect);
     }
   }, [userInfo, redirect, navigate]);
 
  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val).trim());

 const submitHandler = async (e) => {
    e.preventDefault()     
    if (!name.trim()) return toast.error('Please enter your name');
    if (!isValidEmail(email)) return toast.error('Please enter a valid email');
    if (password.length < 6) return toast.error('Password must be at least 6 characters long');
    if (password !== confirmPassword) return toast.error('Passwords do not match');

    if (partnerCode.trim() && partnerStatus?.valid === false) {
      return toast.error('Please enter a valid partner code or clear the field.');
    }
    try {
      const payload = {
        name: name.trim(),
        email: String(email).trim().toLowerCase(),
        password,
        partnerCode: partnerCode.trim() || undefined,
      };
      const res = await register(payload).unwrap();
      dispatch(setCredentials({ ...res }));
      toast.success('Registration successful');
      navigate(redirect);
    } catch (err) {
      // surface server validation errors
      const msg = err?.data?.message || err?.data?.errors?.[0]?.msg || err.error || 'Registration failed';
      toast.error(msg);
   }
   }

  const handlePartnerCodeValidate = async () => {
    const code = partnerCode.trim();
    if (!code) {
      setPartnerStatus(null);
      return;
    }
    try {
      setIsValidating(true);
      const result = await validatePartnerCode({ code }).unwrap();
      setPartnerStatus(result);
      if (result.valid) {
        toast.success(`Partner code applied: ${result.tierName || 'Tier'}`);
      }
    } catch (err) {
      const errorCode = err?.data?.errorCode || err?.data?.code;
      const message = err?.data?.message || 'Partner code validation failed.';
      setPartnerStatus({ valid: false, errorCode });
      toast.error(message);
    } finally {
      setIsValidating(false);
    }
  };
     
 
   return (
     <FormContainer>
       <h1>Sign Up</h1>
 
       <Form onSubmit={submitHandler}>
         <Form.Group controlId='name' className="my-3">
           <Form.Label>Name</Form.Label>
           <Form.Control
             type='text'
             placeholder='Enter name'
             value={name}
             onChange={(e) => setName(e.target.value)}
           ></Form.Control>
         </Form.Group>
 
         <Form.Group controlId='email' className="my-3">
           <Form.Label>Email Address</Form.Label>
           <Form.Control
             type='email'
             placeholder='Enter email'
             value={email}
             onChange={(e) => setEmail(e.target.value)}
           ></Form.Control>
         </Form.Group>
 
         <Form.Group controlId='password' className="my-3">
           <Form.Label>Password</Form.Label>
           <Form.Control
             type='password'
             placeholder='Enter password'
             value={password}
             onChange={(e) => setPassword(e.target.value)}
           ></Form.Control>
         </Form.Group>
 
        <Form.Group controlId='confirmPassword' className="my-3">
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type='password'
            placeholder='Confirm password'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          ></Form.Control>
        </Form.Group>

        <Form.Group controlId="partnerCode" className="my-3">
          <Form.Label>Partner Code (optional)</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Enter partner code"
              value={partnerCode}
              onChange={(e) => {
                setPartnerCode(e.target.value.toUpperCase());
                setPartnerStatus(null);
              }}
              onBlur={handlePartnerCodeValidate}
            />
            <Button
              variant="outline-secondary"
              onClick={handlePartnerCodeValidate}
              disabled={isValidating}
            >
              {isValidating ? 'Checking...' : 'Validate'}
            </Button>
          </InputGroup>
          {partnerStatus?.valid && (
            <div className="mt-2">
              <Badge bg="success">Valid</Badge>{' '}
              <span className="text-muted">
                Tier: {partnerStatus.tierName || 'Partner'}
              </span>
            </div>
          )}
          {partnerStatus?.valid === false && (
            <div className="mt-2 text-danger small">
              Code invalid{partnerStatus.errorCode ? ` (${partnerStatus.errorCode})` : ''}.
            </div>
          )}
          <Form.Text className="text-muted">
            Have a partner referral code? Enter it here to unlock tier pricing.
          </Form.Text>
        </Form.Group>
 
         <Button 
           type='submit' 
           variant='primary' 
           className="mt-2"
           disabled={ isLoading }
         >
           Register
         </Button>
 
         { isLoading && <Loader /> }
       </Form> 
 
       <Row className='py-3'>
         <Col>
          Already have an account?{' '}
           <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>
            Login
           </Link>
         </Col> 
       </Row>
     </FormContainer>
   )
 }
 
 export default RegisterScreen;
